import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth.service';
import { consultantsService, ConsultantService } from '../../services/consultants.service';
import StatsCard from '../../components/dashboard/StatsCard';
import { 
  Briefcase, 
  Users, 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  FileText,
  Eye,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity,
  MessageSquare,
  Target,
  Award
} from 'lucide-react';

interface ConsultantInfo {
  id: number;
  name?: string;
  company_name?: string;
  phone_number?: string;
  about?: string;
  address?: string;
  website?: string;
  social_media_links?: string;
  category_id?: string;
  country?: string;
  wilaya_id?: number;
}

interface ConsultantStats {
  totalServices: number;
  activeServices: number;
  completedServices: number;
  totalViews: number;
  averageRating: number;
  totalReviews: number;
  totalRevenue: number;
  pendingQuotes: number;
  activeClients: number;
  completedProjects: number;
}

const StatsPage: React.FC = () => {
  const { user } = useAuth();
  const [consultant, setConsultant] = useState<ConsultantInfo | null>(null);
  const [stats, setStats] = useState<ConsultantStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch consultant info
        const consultantInfo = await authService.getTrainingCenterInfo(); // Using training center endpoint for consultant
        setConsultant(consultantInfo);
        
        // Fetch consultant services
        const services = await consultantsService.getMyServices();
        
        // Calculate statistics from services
        const consultantStats: ConsultantStats = {
          totalServices: services.services.length,
          activeServices: services.services.filter(s => s.status === 'approved').length,
          completedServices: services.services.filter(s => s.status === 'approved').length,
          totalViews: services.services.reduce((sum, s) => sum + (s.views || 0), 0),
          averageRating: services.services.length > 0 ? services.services.reduce((sum, s) => sum + (s.rating || 0), 0) / services.services.length : 0,
          totalReviews: services.services.reduce((sum, s) => sum + (s.reviews || 0), 0),
          totalRevenue: Math.floor(Math.random() * 500000) + 100000, // Mock data
          pendingQuotes: Math.floor(Math.random() * 10), // Mock data
          activeClients: Math.floor(Math.random() * 20) + 5, // Mock data
          completedProjects: Math.floor(Math.random() * 50) + 20, // Mock data
        };
        
        setStats(consultantStats);
      } catch (err) {
        setError('Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-6">
                  <div className="h-16 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Erreur de chargement</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Statistiques Consultant
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Vue d'ensemble des performances de {consultant?.company_name || consultant?.name}
          </p>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Total Services */}
          <StatsCard
            name="Prestations Total"
            value={stats?.totalServices || 0}
            change="+12%"
            changeType="positive"
            icon="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            color="blue"
          />

          {/* Active Services */}
          <StatsCard
            name="Prestations Actives"
            value={stats?.activeServices || 0}
            change="+8%"
            changeType="positive"
            icon="M13 10V3L4 14h7v7l9-11h-7z"
            color="emerald"
          />

          {/* Completed Services */}
          <StatsCard
            name="Projets Terminés"
            value={stats?.completedProjects || 0}
            change="+15%"
            changeType="positive"
            icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            color="purple"
          />

          {/* Total Views */}
          <StatsCard
            name="Vues Total"
            value={stats?.totalViews || 0}
            change="+23%"
            changeType="positive"
            icon="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            color="pink"
          />

          {/* Average Rating */}
          <StatsCard
            name="Note Moyenne"
            value={stats?.averageRating ? stats.averageRating.toFixed(1) : '0.0'}
            change="+0.3"
            changeType="positive"
            icon="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            color="orange"
          />

          {/* Total Reviews */}
          <StatsCard
            name="Avis Total"
            value={stats?.totalReviews || 0}
            change="+18%"
            changeType="positive"
            icon="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            color="blue"
          />

          {/* Total Revenue */}
          <StatsCard
            name="Revenus Total"
            value={`${stats?.totalRevenue ? (stats.totalRevenue / 1000).toFixed(0) : 0}K DZD`}
            change="+25%"
            changeType="positive"
            icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            color="emerald"
          />

          {/* Active Clients */}
          <StatsCard
            name="Clients Actifs"
            value={stats?.activeClients || 0}
            change="+5%"
            changeType="positive"
            icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            color="purple"
          />
        </div>

        {/* Additional Stats Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Performance Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Aperçu des Performances</h3>
              <BarChart3 className="h-6 w-6 text-brand-primary" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Taux d'approbation</span>
                <span className="text-sm font-semibold text-emerald-600">92%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '92%' }}></div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Engagement client</span>
                <span className="text-sm font-semibold text-blue-600">78%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: '78%' }}></div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Satisfaction client</span>
                <span className="text-sm font-semibold text-purple-600">85%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: '85%' }}></div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Activité Récente</h3>
              <Activity className="h-6 w-6 text-brand-primary" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Nouvelle prestation approuvée</p>
                  <p className="text-xs text-gray-500">Il y a 2 heures</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">3 nouvelles demandes de devis</p>
                  <p className="text-xs text-gray-500">Il y a 4 heures</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Star className="h-4 w-4 text-purple-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Nouvel avis 5 étoiles</p>
                  <p className="text-xs text-gray-500">Il y a 6 heures</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <Eye className="h-4 w-4 text-orange-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Prestation vue 50 fois</p>
                  <p className="text-xs text-gray-500">Il y a 8 heures</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Rapides</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="flex items-center justify-center space-x-2 p-4 bg-brand-primary/10 text-brand-primary rounded-lg hover:bg-brand-primary/20 transition-colors">
              <Briefcase className="h-5 w-5" />
              <span className="text-sm font-medium">Nouvelle Prestation</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-4 bg-emerald-100 text-emerald-600 rounded-lg hover:bg-emerald-200 transition-colors">
              <MessageSquare className="h-5 w-5" />
              <span className="text-sm font-medium">Demandes de Devis</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-4 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
              <Target className="h-5 w-5" />
              <span className="text-sm font-medium">Projets en Cours</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-4 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors">
              <Award className="h-5 w-5" />
              <span className="text-sm font-medium">Portfolio</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
